import classNames from 'classnames';
import PropTypes from 'prop-types';

import { ChevronDownSmall } from '@vimeo/bokeh-icons';
import { Badge } from '@vimeo/iris/components';
import { Header, Paragraph } from '@vimeo/iris/typography';

import irisMarkdown from 'src/_components/Markdown';
import { ucwords } from 'src/_tools/helpers';

import styles from './ReferenceGroup.module.scss';

const Parameters = (props) => {
  const { isPlayground = false, specification } = props;

  const parameters =
    typeof specification.parameters !== 'undefined' ? specification.parameters : [];
  const requestBody =
    typeof specification.requestBody !== 'undefined' ? specification.requestBody : [];

  if (Array.isArray(parameters) && Array.isArray(requestBody)) {
    return null;
  }

  // Break up the parameter spec so we can show them separately to the user.
  let pathParameters = {};
  let queryParameters = {};
  const fieldsQueryParam = {
    description: 'The fields that are requested in the response.',
    in: 'query',
    required: false,
    schema: {
      type: 'array',
    },
  };
  if (!parameters['fields']) {
    parameters['fields'] = fieldsQueryParam;
  }

  if (!Array.isArray(parameters)) {
    Object.keys(parameters).map(function (name) {
      if (parameters[name].in === 'path') {
        pathParameters[name] = parameters[name];
      } else if (parameters[name].in === 'query') {
        // add 'fields' as a query param only for playground unless private or required param
        if (
          name != 'fields' ||
          isPlayground ||
          parameters[name]['x-devsite-visibility-private'] ||
          parameters[name].required == true
        ) {
          queryParameters[name] = parameters[name];
        }
      }
    });
    queryParameters = Object.keys(queryParameters)
      .sort()
      .reduce((result, key) => ((result[key] = queryParameters[key]), result), {});
  }

  const getParameterTypeForDisplay = (payload, name, parameter) => {
    const fieldType =
      parameter.in === 'path' ? 'segment' : payload === 'query' ? 'param' : 'bodyparam';

    if (!isPlayground) {
      return ucwords(parameter.schema.type);
    } else if (typeof parameter.schema.enum === 'undefined') {
      let defaultValue;

      if (devsite?.user && name === 'user_id') {
        defaultValue = devsite?.user?.id;
      } else {
        defaultValue = '';
      }

      return (
        <input
          name={name}
          className={classNames(styles.param_input, fieldType)}
          placeholder={ucwords(parameter.schema.type)}
          defaultValue={defaultValue}
          required
        />
      );
    }

    let enumOptions = [
      {
        value: '',
        label: 'Choose one',
      },
    ];

    Object.keys(parameter.schema.enum).map(function (i) {
      enumOptions.push({
        value: parameter.schema.enum[i],
        label: parameter.schema.enum[i],
      });
    });

    return (
      <>
        <select
          name={name}
          className={classNames(styles.select, fieldType)}
          showLabel={false}
          errorMsg={parameter.required ? 'Please select an option.' : ''}
        >
          {enumOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDownSmall className={styles.down_arrow} />
      </>
    );
  };

  const getEnumList = (description, parameter) => {
    if (typeof parameter.schema.enum === 'undefined') {
      return '';
    }

    // remove enums from the list that are already included in the description
    const filteredList = Object.keys(parameter.schema.enum).filter((key) => {
      const value = parameter.schema.enum[key];
      return !description.includes(`* \`${value}\``);
    });

    if (filteredList.length > 0) {
      return (
        <ul>
          {filteredList.map(function (i) {
            return (
              <li key={i}>
                <code>{parameter.schema.enum[i]}</code>
              </li>
            );
          })}
        </ul>
      );
    }
    return '';
  };

  const getCapabilitiesForParameter = (parameter) => {
    const vendorTags = (parameter['x-devsite-vendor-tags'] || []).map((tag) =>
      tag.replace('capability:', ''),
    );

    if (vendorTags.length === 0) {
      return;
    }

    let capabilityString = '';
    if (vendorTags.length === 1) {
      capabilityString = `capability ${vendorTags[0]}`;
    }
    if (vendorTags.length > 1) {
      capabilityString = `capabilities ${vendorTags.join(' and ')}`;
    }

    if (capabilityString) {
      return (
        <div style={{ fontStyle: 'italic' }}>
          {`This parameter requires an app with the ${capabilityString}`}
        </div>
      );
    }

    return capabilityString;
  };

  const getBadgeForParameters = (has_private_visibility, has_capability_visibility) => {
    let private_badge = (
      <Badge
        href="#"
        format="live"
        size="sm"
        style={{ margin: '0.25rem 0.5rem', display: 'inline-flex' }}
      >
        Private
      </Badge>
    );
    let capability_badge = (
      <Badge
        href="#"
        format="live"
        size="sm"
        style={{ margin: '0.25rem 0.5rem', display: 'inline-flex' }}
      >
        Capability
      </Badge>
    );

    if (has_private_visibility && has_capability_visibility) {
      return capability_badge;
    }

    if (has_capability_visibility) {
      return capability_badge;
    }

    if (has_private_visibility) {
      return private_badge;
    }

    return false;
  };

  const getParameterContent = (payload, name, parameter, i) => {
    const description = parameter.description.replace(/Option descriptions:/, '');

    let has_private_visibility =
      typeof parameter['x-devsite-visibility-private'] !== 'undefined' &&
      parameter['x-devsite-visibility-private'];
    let has_capability_visibility =
      typeof parameter['x-devsite-vendor-tags'] !== 'undefined' &&
      parameter['x-devsite-vendor-tags'];
    let is_deprecated =
      typeof parameter['x-devsite-deprecated'] !== 'undefined' && parameter['x-devsite-deprecated'];

    return (
      <tr key={`parameter-${i}`}>
        <td className={styles.form_row} style={{ width: !isPlayground ? '40%' : '50%' }}>
          {name}
          {parameter.required && <span style={{ color: 'red' }}> *</span>}
          {getBadgeForParameters(has_private_visibility, has_capability_visibility)}
        </td>
        <td className={styles.form_row} style={{ width: !isPlayground ? '20%' : '50%' }}>
          {getParameterTypeForDisplay(payload, name, parameter)}
        </td>
        {!isPlayground && (
          <td className={styles.param_description} style={{ width: !isPlayground ? '40%' : '50%' }}>
            {irisMarkdown(description, false)}
            {getEnumList(description, parameter)}
            {getCapabilitiesForParameter(parameter)}
            {is_deprecated && irisMarkdown('This field is deprecated.')}
          </td>
        )}
      </tr>
    );
  };

  const getParameterTableBlock = (header, content) => {
    return (
      <div>
        <Header size="4">{header}</Header>

        <table>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>{isPlayground ? 'Value' : 'Data type'}</th>
              {!isPlayground && <th>Description</th>}
            </tr>
          </thead>
          <tbody>{content}</tbody>
        </table>

        <Paragraph size="3" className={styles.param_required}>
          <span style={{ color: 'red' }}>*</span> Required
        </Paragraph>
      </div>
    );
  };

  return (
    <div>
      {!Array.isArray(parameters) && (
        <div>
          {Object.keys(pathParameters).length > 0 &&
            getParameterTableBlock(
              'Path parameters',
              Object.keys(pathParameters).map(function (name, ii) {
                return getParameterContent('path', name, pathParameters[name], ii);
              }),
            )}

          {Object.keys(queryParameters).length > 0 &&
            getParameterTableBlock(
              'Query parameters',
              Object.keys(queryParameters).map(function (name, ii) {
                return getParameterContent('query', name, queryParameters[name], ii);
              }),
            )}
        </div>
      )}

      {!Array.isArray(requestBody) &&
        getParameterTableBlock(
          'Body',
          Object.keys(requestBody.content).map(function (contentType) {
            let requestParams = requestBody.content[contentType];
            return Object.keys(requestParams).map(function (name, ii) {
              return getParameterContent('payload', name, requestParams[name], ii);
            });
          }),
        )}
    </div>
  );
};

Parameters.propTypes = {
  isPlayground: PropTypes.bool,
  specification: PropTypes.any.isRequired,
};

export default Parameters;
